import { Card, Grid, Select, Stack, Title, Tooltip } from "@mantine/core";
import { IConsequenceWithId, IEvaluationCategoryApi, IIssueBoardApi, IRisikoProbability } from "@mcapps/shared";
import { IIssueApi, IIssueUpdateSchemaProps } from "@schemas/issueSchema";
import { useCallback, useMemo } from "react";
import { usePutProjectByProjectIdIssueUpdateSimpleMutation } from "../../../../../../../openapi";
import { McTitle } from "../../../../../../components";
import FoldableSection from "../../../../../../components/FoldableSection/FoldableSection";
import McTextAreaNew from "../../../../../../components/McInput/McTextAreaNew";
import IssueRisikoMatrix from "../../../../../../components/RisikoMatrix/IssueRisikoMatrix";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { createSimplePayload, guardedIssueUpdate } from "./modalUtils";

type IEvaluationCategoryWithId = IEvaluationCategoryApi & { id: string };

export const IssueItemRisikoSectionMantine: React.FC<{
  issue: IIssueApi;
  issueBoard: IIssueBoardApi;
  projectId: string;
}> = ({ issue, issueBoard, projectId }) => {
  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const buildSimplePayload = useMemo(() => createSimplePayload(projectId, issue._id), [projectId, issue._id]);
  const updateSimple = useCallback(
    async (prop: IIssueUpdateSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateValue,
        buildPayload: buildSimplePayload,
      });
    },
    [issue, updateValue, buildSimplePayload],
  );

  const probabilities: IRisikoProbability[] = Object.entries(issueBoard.risikoSection?.probabilites ?? {}).map(
    ([key, value]) => ({
      ...value,
      id: key,
    }),
  );

  const current: IRisikoProbability = probabilities.find((p: IRisikoProbability) => p.id === issue.risiko?.probability);
  const probabilityAfterTiltak: IRisikoProbability = probabilities.find(
    (p: IRisikoProbability) => p.id === issue.risiko?.probabilityAfterTiltak,
  );
  const hasProbabilityAfterTiltak = issueBoard.risikoSection?.enableProbabilityAfterTiltak === true;
  const probabilityOptions = probabilities.filter((p) => p.isActive).map((p) => ({ label: p.name, value: p.id }));
  const probabilityDescription = (optionValue: string) =>
    probabilities.find((probability) => probability.id === optionValue)?.description;

  const categories = useMemo(() => {
    if (issueBoard.risikoSection?.evaluationCategoriesConfig == null) {
      return [];
    }

    const _categories: [string, IEvaluationCategoryApi][] = Object.entries(
      issueBoard.risikoSection.evaluationCategoriesConfig,
    );

    if (_categories.length === 0) {
      return [];
    }

    const _cols = _categories.reduce((acc, [key, value]) => {
      if (value.isActive === false) {
        return acc;
      }

      acc.push({
        id: key,
        ...value,
      });

      return acc;
    }, [] as IEvaluationCategoryWithId[]);

    return _cols;
  }, [issueBoard.risikoSection?.evaluationCategoriesConfig]);

  const renderConsequence = (row: IEvaluationCategoryWithId, afterTiltak = false, includeCategoryName = true) => {
    const consequences: IConsequenceWithId[] = Object.entries(row.consequences)
      .map(([key, value]) => ({ ...value, id: key }))
      .filter((consequence) => consequence.isActive);
    const selectedConsequence = afterTiltak
      ? issue.risiko?.consequenceAfterTiltak?.[row.id]
      : issue.risiko?.consequence?.[row.id];
    const selectedValue =
      selectedConsequence != null ? { ...row.consequences[selectedConsequence], id: selectedConsequence } : null;
    const consequenceDescriptions = Object.fromEntries(
      consequences.map((consequence) => [consequence.id, consequence.description]),
    );

    return (
      <McTitle
        title={
          hasProbabilityAfterTiltak
            ? `${includeCategoryName ? `${row.name || "-"} ` : ""}${afterTiltak ? "Etter" : "Før"} tiltak`
            : row.name || "-"
        }
      >
        <Stack gap="sm">
          <Select
            allowDeselect={false}
            value={selectedValue?.id}
            data={consequences.map((consequence) => ({ label: consequence.name, value: consequence.id }))}
            renderOption={({ option }) => (
              <Tooltip label={consequenceDescriptions[option.value]} disabled={!consequenceDescriptions[option.value]}>
                <div style={{ width: "100%" }}>{option.label}</div>
              </Tooltip>
            )}
            onChange={(newValue) =>
              updateSimple({
                risiko: afterTiltak
                  ? { consequenceAfterTiltak: { [row.id]: newValue } }
                  : { consequence: { [row.id]: newValue } },
              })
            }
          />
          <LocalErrorBoundary>
            <IssueRisikoMatrix
              issue={issue}
              issueBoard={issueBoard}
              evaluationCategoryCapital={row.id}
              afterTiltak={afterTiltak}
            />
          </LocalErrorBoundary>
        </Stack>
      </McTitle>
    );
  };

  return (
    <FoldableSection storageKey="issue-modal-risiko">
      <FoldableSection.Header>
        <Title order={6}>Risiko</Title>
      </FoldableSection.Header>
      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Grid flex={1}>
            {hasProbabilityAfterTiltak ? (
              <>
                <Grid.Col span={12}>
                  <Card withBorder p="sm">
                    <Title order={5} mb="xs">
                      Sannsynlighet
                    </Title>
                    <Grid>
                      <Grid.Col span={5}>
                        <McTitle title="Før tiltak">
                          <Select
                            allowDeselect={false}
                            value={current?.id}
                            data={probabilityOptions}
                            renderOption={({ option }) => {
                              const description = probabilityDescription(option.value);
                              return (
                                <Tooltip label={description} disabled={!description}>
                                  <div style={{ width: "100%" }}>{option.label}</div>
                                </Tooltip>
                              );
                            }}
                            onChange={(newValue) => updateSimple({ risiko: { probability: newValue } })}
                          />
                        </McTitle>
                      </Grid.Col>
                      <Grid.Col span={2} />
                      <Grid.Col span={5}>
                        <McTitle title="Etter tiltak">
                          <Select
                            allowDeselect={false}
                            value={probabilityAfterTiltak?.id}
                            data={probabilityOptions}
                            renderOption={({ option }) => {
                              const description = probabilityDescription(option.value);
                              return (
                                <Tooltip label={description} disabled={!description}>
                                  <div style={{ width: "100%" }}>{option.label}</div>
                                </Tooltip>
                              );
                            }}
                            onChange={(newValue) => updateSimple({ risiko: { probabilityAfterTiltak: newValue } })}
                          />
                        </McTitle>
                      </Grid.Col>
                    </Grid>
                  </Card>
                </Grid.Col>
                {categories.map((row) => (
                  <Grid.Col key={row.id} span={12}>
                    <Card withBorder p="sm">
                      <Title order={5} mb="xs">
                        {row.name || "-"}
                      </Title>
                      <Grid>
                        <Grid.Col span={5}>{renderConsequence(row, false, false)}</Grid.Col>
                        <Grid.Col span={2} />
                        <Grid.Col span={5}>{renderConsequence(row, true, false)}</Grid.Col>
                      </Grid>
                    </Card>
                  </Grid.Col>
                ))}
              </>
            ) : (
              <>
                <Grid.Col span={4}>
                  <Card withBorder p="md">
                    <McTitle title="Sannsynlighet">
                      <Select
                        allowDeselect={false}
                        value={current?.id}
                        data={probabilityOptions}
                        renderOption={({ option }) => {
                          const description = probabilityDescription(option.value);
                          return (
                            <Tooltip label={description} disabled={!description}>
                              <div style={{ width: "100%" }}>{option.label}</div>
                            </Tooltip>
                          );
                        }}
                        onChange={(newValue) => updateSimple({ risiko: { probability: newValue } })}
                      />
                    </McTitle>
                  </Card>
                </Grid.Col>
                <Grid.Col span={8} />
                {categories.map((row) => (
                  <Grid.Col key={row.id} span={4}>
                    <Card withBorder p="md">
                      {renderConsequence(row)}
                    </Card>
                  </Grid.Col>
                ))}
              </>
            )}
            <Grid.Col span={12}>
              <McTitle title="Kommentar">
                <McTextAreaNew
                  onBlur={(e) => updateSimple({ risiko: { categoryComment: e.target.value } })}
                  value={issue.risiko?.categoryComment}
                />
              </McTitle>
            </Grid.Col>
          </Grid>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};
